import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { COUNTRIES } from '../data/countries';
import { useOverridesRefresh } from '../hooks/useOverrides';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { key: 'hero',  label: 'Country hero',   hint: '1400×900 · used on country detail + listing card' },
  { key: 'city0', label: 'City 1 card',    hint: '900×600 · first city in Place section' },
  { key: 'city1', label: 'City 2 card',    hint: '900×600 · second city' },
  { key: 'city2', label: 'City 3 card',    hint: '900×600 · third city' },
];

const DATA_CELL_LABELS = [
  'Cell 1 (typically Income requirement)',
  'Cell 2 (typically Processing time)',
  'Cell 3 (typically Visa validity)',
  'Cell 4 (typically Path to citizenship)',
];

export default function AdminImages() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nc_admin_token') : null;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('images');
  const [slug, setSlug] = useState(COUNTRIES[0]?.slug || '');
  const [overrides, setOverrides] = useState({});           // { hero: url, cities: [url|null,...] }
  const [allRows, setAllRows] = useState([]);                // admin list of every override
  const [busy, setBusy] = useState(null);                    // role currently uploading/deleting
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshGlobal = useOverridesRefresh();
  const fileRefs = { hero: useRef(), city0: useRef(), city1: useRef(), city2: useRef() };

  // Visa-facts editor state
  const [defaultCells, setDefaultCells] = useState([]);      // from country-pages/<slug>.js
  const [textCells, setTextCells] = useState([{}, {}, {}, {}]); // current edits (4 cells)
  const [textBaseline, setTextBaseline] = useState([{}, {}, {}, {}]); // last-saved snapshot
  const [savingText, setSavingText] = useState(false);
  const [textStatus, setTextStatus] = useState('');

  const country = useMemo(() => COUNTRIES.find(c => c.slug === slug), [slug]);

  // Load all overrides (admin) once + per slug detail
  useEffect(() => {
    let cancelled = false;
    setError('');
    Promise.all([
      axios.get(`${API}/admin/country-overrides`, { headers }).catch(() => ({ data: { overrides: [] } })),
      axios.get(`${API}/country-overrides/${slug}`).catch(() => ({ data: { images: {}, text: {} } })),
    ]).then(([listRes, detailRes]) => {
      if (cancelled) return;
      setAllRows(listRes.data.overrides || []);
      const imgs = detailRes.data.images || {};
      setOverrides({
        hero: imgs.hero || null,
        cities: imgs.cities || [null, null, null],
      });
      const cells = (detailRes.data.text || {}).dataCells || [];
      const four = [0, 1, 2, 3].map(i => cells[i] || {});
      setTextCells(four);
      setTextBaseline(four);
    }).catch(e => !cancelled && setError(e?.message || 'Load failed'));
    return () => { cancelled = true; };
  }, [slug, headers, refreshKey]);

  // Lazy-load the country's data file to surface default dataCells in the editor
  useEffect(() => {
    let cancelled = false;
    setDefaultCells([]);
    import(`../data/country-pages/${slug}.js`)
      .then(mod => {
        if (cancelled) return;
        const cells = mod?.default?.hero?.dataCells || [];
        setDefaultCells(cells);
      })
      .catch(() => { /* legacy hand-built page without a data file — leave empty */ });
    return () => { cancelled = true; };
  }, [slug]);

  const upload = async (role, file) => {
    if (!file) return;
    setBusy(role); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/admin/country-overrides/${slug}/image/${role}`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setRefreshKey(k => k + 1);
      refreshGlobal();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally {
      setBusy(null);
      if (fileRefs[role]?.current) fileRefs[role].current.value = '';
    }
  };

  const remove = async (role) => {
    setBusy(role); setError('');
    try {
      await axios.delete(`${API}/admin/country-overrides/${slug}/image/${role}`, { headers });
      setRefreshKey(k => k + 1);
      refreshGlobal();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  const renderUrl = (role) => {
    if (role === 'hero') return overrides.hero ? `${BACKEND_URL}${overrides.hero}` : null;
    const idx = parseInt(role.slice(-1), 10);
    const u = overrides.cities?.[idx];
    return u ? `${BACKEND_URL}${u}` : null;
  };

  // ---- Visa-facts handlers ----
  const setCellField = (idx, key, val) => {
    setTextCells(prev => prev.map((c, i) => i === idx ? { ...c, [key]: val } : c));
    setTextStatus('');
  };

  const textIsDirty = useMemo(
    () => JSON.stringify(textCells) !== JSON.stringify(textBaseline),
    [textCells, textBaseline],
  );

  const saveText = async () => {
    setSavingText(true); setError(''); setTextStatus('');
    try {
      // Strip empty values so backend stores a tight payload (and renderer falls back to default).
      const cleaned = textCells.map(c => {
        const out = {};
        ['label', 'value', 'note'].forEach(k => {
          const v = (c?.[k] || '').trim();
          if (v) out[k] = v;
        });
        return out;
      });
      const res = await axios.put(
        `${API}/admin/country-overrides/${slug}/text`,
        { dataCells: cleaned },
        { headers },
      );
      const saved = (res.data?.text?.dataCells || []);
      const four = [0, 1, 2, 3].map(i => saved[i] || {});
      setTextCells(four);
      setTextBaseline(four);
      setTextStatus('Saved · live on the country page after a refresh.');
      refreshGlobal();
      setRefreshKey(k => k + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSavingText(false);
    }
  };

  const resetText = () => {
    setTextCells(textBaseline.map(c => ({ ...c })));
    setTextStatus('');
  };

  const clearAllText = () => {
    setTextCells([{}, {}, {}, {}]);
    setTextStatus('');
  };

  return (
    <div className="admin-images" data-testid="admin-images">
      <div className="admin-images-head">
        <div>
          <h2 style={{ margin: '0 0 4px', fontFamily: '\'Cormorant Garamond\', serif', fontSize: 26, fontWeight: 500 }}>Country CMS</h2>
          <div style={{ color: 'var(--warm-grey)', fontSize: 13 }}>
            Override placeholder images and visa facts per country. {allRows.length} countr{allRows.length === 1 ? 'y' : 'ies'} customised so far.
          </div>
        </div>
      </div>

      <div className="admin-tabs" role="tablist" data-testid="admin-tabs">
        <button
          className={`admin-tab ${tab === 'images' ? 'active' : ''}`}
          role="tab"
          aria-selected={tab === 'images'}
          onClick={() => setTab('images')}
          data-testid="tab-cms-images"
        >Images</button>
        <button
          className={`admin-tab ${tab === 'text' ? 'active' : ''}`}
          role="tab"
          aria-selected={tab === 'text'}
          onClick={() => setTab('text')}
          data-testid="tab-cms-text"
        >Visa facts</button>
      </div>

      <div className="admin-images-picker">
        <label htmlFor="ai-country" style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--brass-deep)', fontWeight: 500, marginRight: 12 }}>Country</label>
        <select
          id="ai-country"
          value={slug}
          onChange={e => setSlug(e.target.value)}
          data-testid="ai-country-select"
          style={{ padding: '10px 14px', border: '1px solid rgba(95,86,53,0.25)', borderRadius: 10, fontSize: 14, background: '#fff', minWidth: 280 }}
        >
          {COUNTRIES.map(c => (
            <option key={c.slug} value={c.slug}>{c.flag} {c.name} — {c.visaName}</option>
          ))}
        </select>
        {country && (
          <a href={`/countries/${country.slug}`} target="_blank" rel="noreferrer" style={{ marginLeft: 14, fontSize: 13, color: 'var(--brass-deep)' }} data-testid="ai-preview-link">
            Preview live page →
          </a>
        )}
      </div>

      {error && <div className="admin-error" data-testid="ai-error" style={{ marginTop: 12 }}>{error}</div>}

      {tab === 'images' && (
        <div className="admin-images-grid">
          {ROLES.map(role => {
            const url = renderUrl(role.key);
            const fallback = role.key === 'hero'
              ? country?.image
              : (country?.image);
            return (
              <div key={role.key} className="admin-image-card" data-testid={`ai-card-${role.key}`}>
                <div className="admin-image-card-head">
                  <strong>{role.label}</strong>
                  <span>{role.hint}</span>
                </div>
                <div className="admin-image-preview">
                  {url ? (
                    <img src={url} alt={`${country?.name} ${role.label}`} data-testid={`ai-preview-${role.key}`} />
                  ) : (
                    <div className="admin-image-placeholder">
                      {fallback ? <img src={fallback} alt="(default placeholder)" style={{ opacity: 0.45 }} /> : null}
                      <span>No custom image yet — site uses default</span>
                    </div>
                  )}
                </div>
                <div className="admin-image-actions">
                  <input
                    ref={fileRefs[role.key]}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={e => upload(role.key, e.target.files?.[0])}
                    disabled={busy === role.key}
                    data-testid={`ai-input-${role.key}`}
                  />
                  {url && (
                    <button
                      className="admin-image-remove"
                      onClick={() => remove(role.key)}
                      disabled={busy === role.key}
                      data-testid={`ai-remove-${role.key}`}
                    >
                      {busy === role.key ? 'Working…' : 'Remove'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'text' && (
        <div data-testid="ai-text-editor">
          <div className="admin-text-help">
            <strong>What this edits:</strong> the four data cells in the country page hero (income / processing / validity / citizenship path). Leave any field empty to keep the default value from the page data file. HTML is allowed in <em>value</em> (e.g. <code>€3,680&lt;em&gt;/mo&lt;/em&gt;</code>) — same as the underlying data files. Changes go live after a hard refresh on the country page.
          </div>

          <div className="admin-text-grid">
            {[0, 1, 2, 3].map(i => {
              const def = defaultCells[i] || {};
              const cell = textCells[i] || {};
              return (
                <div key={i} className="admin-text-card" data-testid={`text-cell-${i}`}>
                  <h4>{DATA_CELL_LABELS[i]}</h4>

                  <div className="field">
                    <label htmlFor={`cell-${i}-label`}>Label</label>
                    <input
                      id={`cell-${i}-label`}
                      type="text"
                      value={cell.label || ''}
                      placeholder={def.label || ''}
                      onChange={e => setCellField(i, 'label', e.target.value)}
                      data-testid={`text-cell-${i}-label`}
                    />
                    {def.label && (
                      <span className="placeholder-default">Default: {def.label}</span>
                    )}
                  </div>

                  <div className="field">
                    <label htmlFor={`cell-${i}-value`}>Value (HTML allowed)</label>
                    <input
                      id={`cell-${i}-value`}
                      type="text"
                      value={cell.value || ''}
                      placeholder={def.value || ''}
                      onChange={e => setCellField(i, 'value', e.target.value)}
                      data-testid={`text-cell-${i}-value`}
                    />
                    {def.value && (
                      <span className="placeholder-default">Default: {def.value}</span>
                    )}
                  </div>

                  <div className="field">
                    <label htmlFor={`cell-${i}-note`}>Note</label>
                    <input
                      id={`cell-${i}-note`}
                      type="text"
                      value={cell.note || ''}
                      placeholder={def.note || ''}
                      onChange={e => setCellField(i, 'note', e.target.value)}
                      data-testid={`text-cell-${i}-note`}
                    />
                    {def.note && (
                      <span className="placeholder-default">Default: {def.note}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="admin-text-actions">
            <button
              className="admin-text-save"
              onClick={saveText}
              disabled={savingText || !textIsDirty}
              data-testid="text-save"
            >
              {savingText ? 'Saving…' : 'Save visa facts'}
            </button>
            <button
              className="admin-text-reset"
              onClick={resetText}
              disabled={savingText || !textIsDirty}
              data-testid="text-reset"
            >Reset edits</button>
            <button
              className="admin-text-reset"
              onClick={clearAllText}
              disabled={savingText}
              data-testid="text-clear"
            >Clear all overrides</button>
            {textStatus && <span className="admin-text-status" data-testid="text-status">{textStatus}</span>}
          </div>
        </div>
      )}

      {allRows.length > 0 && (
        <div className="admin-images-summary" data-testid="ai-summary">
          <h3 style={{ marginTop: 32, fontFamily: '\'Cormorant Garamond\', serif', fontSize: 20, fontWeight: 500 }}>Already customised</h3>
          <table className="admin-table">
            <thead><tr><th>Country</th><th>Hero</th><th>City cards</th><th>Text fields</th><th>Last updated</th></tr></thead>
            <tbody>
              {allRows.map(r => {
                const meta = COUNTRIES.find(c => c.slug === r.slug);
                return (
                  <tr key={r.slug} onClick={() => setSlug(r.slug)} style={{ cursor: 'pointer' }}>
                    <td><strong>{meta?.flag} {meta?.name || r.slug}</strong></td>
                    <td>{r.has_hero ? '✓' : '—'}</td>
                    <td>{r.city_count}/3</td>
                    <td>{r.text_field_count || 0}</td>
                    <td style={{ color: 'var(--warm-grey)', fontSize: 12 }}>{r.updated_at ? new Date(r.updated_at).toLocaleString() : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
